<template>
    <div>
        <pc-input
            v-model="name"
            alt-label="nameInput"
            class="mb-3"
            required
            v-bind:label="nameLabel"
            bold-label
            v-bind:validator="requiredValidator"
            v-on:validated="onValidated(validatorKeys.displayName, $event)" />
        <pc-radio-group
            v-model="tvrType"
            v-bind:disabled="disableTVRTypeOptions"
            v-bind:options="tvrTypeOptions"
            name="tvrTypeOptions"
            class="mb-3"
            alt-label="tvrTypeOptions"
            v-bind:label="tvrTypeLabel"
            bold-label />
        <pc-alert
            v-bind:shown="disableTVRTypeOptions"
            v-bind:severity="alertSeverityInfo">
            {{ editingTextValidationRuleTypeDisabled }}
        </pc-alert>
        <pc-alert
            v-model:shown="showBlockSaveAlert"
            v-bind:severity="alertSeverityError"
            dismissible>
            {{ blockSaveMessage }}
        </pc-alert>
        <div v-show="showPatternMatchInput">
            <pattern-match-key />
            <div class="align-items-center mt-3">
                <pc-label
                    v-bind:label="patternMatchInputLabel"
                    class="fw-bold" />
                <pc-button-icon
                    link
                    class="text-primary bg-white"
                    v-bind:outline="false"
                    icon="plus-circle"
                    v-on:click="addNewPatternInput">
                    {{ addPatternLabel }}
                </pc-button-icon>
            </div>
            <div
                v-for="(pattern, i) in patternMatchValues"
                v-bind:key="pattern + i"
                class="d-flex align-items-top mb-3">
                <pc-input
                    v-model="pattern.value"
                    alt-label="pattern-input"
                    v-bind:validator="requiredValidator"
                    class="w-75" />
                <div class="mx-2">
                    <pc-bs-icon
                        v-if="patternMatchValues.length > 1"
                        class="mt-2"
                        style="cursor: pointer"
                        name="trash"
                        v-on:click="deletePattern(i)" />
                </div>
            </div>
        </div>
        <pc-alert
            v-bind:shown="showCharSetInputHelp"
            v-bind:severity="alertSeverityInfo">
            {{ characterSetsWhitespaceInfoMessage }}
        </pc-alert>
        <div v-show="showCharacterSetsInput">
            <pc-label
                v-bind:label="charSetsInputLabel"
                class="fw-bold"
                show-help
                v-on:help-clicked="showCharSetInputHelp = !showCharSetInputHelp" />
            <div class="mx-3">
                <pc-checkbox
                    v-model="aToZUpper"
                    v-bind:label="aToZUpperLabel" />
                <pc-checkbox
                    v-model="aToZLower"
                    v-bind:label="aToZLowerLabel" />
                <pc-checkbox
                    v-model="numeric"
                    v-bind:label="numericLabel" />
                <pc-checkbox
                    v-model="punctuation"
                    v-bind:label="punctuationLabel" />
                <span class="text-muted mx-4">{{ allowedPunctuationSet }}</span>
            </div>
            <div class="my-3">
                <pc-checkbox
                    v-model="enforceMaxLength"
                    v-bind:label="enforceMaxLengthSwitchLabel"
                    switch
                    class="mb-3"
                    v-on:input="onToggleEnforceMaxLength" />
                <pc-transition v-bind:transition="slideTransition">
                    <pc-input
                        v-show="enforceMaxLength"
                        v-model="maxLength"
                        v-bind:validator="maxLengthValidator"
                        v-bind:label="maxLengthLabel"
                        bold-label
                        required
                        class="w-50"
                        alt-label="maxLengthInput"
                        v-on:validated="onValidated(validatorKeys.maxLength, $event)" />
                </pc-transition>
            </div>
        </div>
        <pc-alert
            v-model:shown="showValidationErrorMessageInfo"
            class="mt-3"
            dismissible>
            {{ validationErrorMessageInfo }}
        </pc-alert>
        <pc-textarea
            v-model="validationErrorMessage"
            v-bind:label="validationErrorMessageLabel"
            v-bind:placeholder="defaultValidationErrorMessage"
            v-bind:maxlength="maxValidationErrorMessageLength"
            class="mb-3"
            show-remaining-characters
            bold-label
            show-help
            v-on:help-clicked="showValidationErrorMessageInfo = !showValidationErrorMessageInfo" />
        <text-validation-rule-test-box
            v-bind:rule-schema="currentRuleSchemaConfiguration"
            v-bind:rule-type="tvrType"
            v-bind:validation-error-message="validationErrorMessage"
            v-bind:default-error-message="defaultValidationErrorMessage"
            v-bind:is-rule-schema-valid="isRuleSchemaValid" />
    </div>
</template>

<script>
    import { TEXT_VALIDATION_RULE_TYPES, TEXT_VALIDATION_RULE_CHAR_SETS } from "./textValidationRulesTab.vue";
    import { mapI18n } from "Util/i18n";
    import { NAMESPACE, KEYS } from "./i18n/textValidationRulesTab.i18n";
    import { VALIDATOR_TYPES, getValidator } from "Validation/validator";
    import { ValidationHub } from "Validation/validationHub";
    import { ALERT_SEVERITY } from "Util/alerts";
    import { collapseAndTrimWhitespace } from "Util/util";
    import textValidationRuleTestBox from "./textValidationRuleTestBox.vue";
    import { TRANSITION_TYPE } from "Util/animations";
    import patternMatchKey from "./patternMatchKey.vue";

    export const EDIT_TVR_RESULT = {
        save: "Save",
        cancel: "Cancel"
    };

    //This number comes from MaxMultiLineStringValLength in the projectDataMgr
    export const MAX_LENGTH_VALUE_LIMIT = 16384;
    const MAX_VALIDATION_ERROR_MESSAGE_LENGTH = 1024;

    export default {
        components: {
            textValidationRuleTestBox,
            patternMatchKey
        },
        provide() {
            return {
                validationHub: this.validationHub
            };
        },
        props: {
            tvr: { type: Object, required: false, default: null },
            allowedPunctuationSet: { type: String, required: true }
        },
        data() {
            return {
                name: "",
                patternMatchValues: [],
                tvrType: TEXT_VALIDATION_RULE_TYPES.CHAR_SET,
                aToZUpper: false,
                aToZLower: false,
                numeric: false,
                punctuation: false,
                testInput: "",
                maxLength: "",
                enforceMaxLength: false,
                validationErrorMessage: "",
                showValidationErrorMessageInfo: false,
                testInputPasses: true,
                showTestRuleResult: false,
                requiredValidator: getValidator(this.$app, VALIDATOR_TYPES.required),
                validationResults: {},
                validatorKeys: { maxLength: 0, displayName: 1 },
                blockSaveMessage: "",
                showBlockSaveAlert: false,
                alertSeverityError: ALERT_SEVERITY.error,
                alertSeverityInfo: ALERT_SEVERITY.info,
                validationHub: new ValidationHub(),
                maxValidationErrorMessageLength: MAX_VALIDATION_ERROR_MESSAGE_LENGTH,
                slideTransition: TRANSITION_TYPE.slide,
                showCharSetInputHelp: false
            };
        },
        computed: {
            maxLengthValidator() {
                if (!this.enforceMaxLength) {
                    return null;
                }
                const validatorOptions = [
                    { type: VALIDATOR_TYPES.required },
                    { type: VALIDATOR_TYPES.positiveInteger },
                    { type: VALIDATOR_TYPES.min, min: 1 },
                    { type: VALIDATOR_TYPES.max, max: MAX_LENGTH_VALUE_LIMIT }
                ];

                return getValidator(this.$app, validatorOptions);
            },
            inputsAreValid() {
                for (const key in this.validationResults) {
                    if (this.doesKeyHaveValidationError(key)) {
                        return false;
                    }
                }
                return true;
            },
            tvrTypeOptions() {
                return [
                    { text: this.charSetTVRType, value: TEXT_VALIDATION_RULE_TYPES.CHAR_SET },
                    { text: this.patternMatchTVRType, value: TEXT_VALIDATION_RULE_TYPES.PATTERN_MATCH }
                ];
            },
            showPatternMatchInput() {
                return this.tvrType === TEXT_VALIDATION_RULE_TYPES.PATTERN_MATCH;
            },
            showCharacterSetsInput() {
                return this.tvrType === TEXT_VALIDATION_RULE_TYPES.CHAR_SET;
            },

            disableTVRTypeOptions() {
                // If this is a char set TVR applied to a multiline text field,
                // don't allow it to be made into a PatternMatch TVR as that is not
                // supported for MLT

                if (
                    this.tvr &&
                    this.tvr.TextValidationRuleTypeId == TEXT_VALIDATION_RULE_TYPES.CHAR_SET &&
                    this.tvr.HasMultiLineTextFieldReferences
                ) {
                    return true;
                }

                return false;
            },
            selectedCharacterSets() {
                const sets = [];

                if (this.aToZUpper) {
                    sets.push(TEXT_VALIDATION_RULE_CHAR_SETS.A_Z_UPPER);
                }

                if (this.aToZLower) {
                    sets.push(TEXT_VALIDATION_RULE_CHAR_SETS.A_Z_LOWER);
                }

                if (this.numeric) {
                    sets.push(TEXT_VALIDATION_RULE_CHAR_SETS.NUMERIC);
                }
                if (this.punctuation) {
                    sets.push(TEXT_VALIDATION_RULE_CHAR_SETS.PUNCTUATION);
                }

                return sets;
            },
            currentRuleSchemaConfiguration() {
                return {
                    allowedCharacterSets: this.selectedCharacterSets,
                    maxLength: this.enforceMaxLength ? Number(this.maxLength) : null,
                    allowedPatterns: this.getAllowedPatterns()
                };
            },
            atLeastOneCharacterSetSelected() {
                return this.selectedCharacterSets && this.selectedCharacterSets.length > 0;
            },
            atLeastOnePatternDefined() {
                return this.patternMatchValues && this.patternMatchValues.length > 0;
            },
            areThereEmptyPatternsInTheConfig() {
                for (const pattern of this.patternMatchValues) {
                    if (collapseAndTrimWhitespace(pattern.value) === "") {
                        return true;
                    }
                }

                return false;
            },
            isRuleSchemaValid() {
                if (this.tvrType === TEXT_VALIDATION_RULE_TYPES.CHAR_SET) {
                    return this.isMaxLengthValid && this.atLeastOneCharacterSetSelected;
                }

                if (this.tvrType === TEXT_VALIDATION_RULE_TYPES.PATTERN_MATCH) {
                    return this.atLeastOnePatternDefined && !this.areThereEmptyPatternsInTheConfig;
                }

                return false;
            },
            isMaxLengthValid() {
                if (this.tvrType !== TEXT_VALIDATION_RULE_TYPES.CHAR_SET || !this.enforceMaxLength) {
                    return true;
                }

                if (isNaN(this.maxLength)) {
                    return false;
                }

                const maxLength = Number(this.maxLength);
                return maxLength <= MAX_LENGTH_VALUE_LIMIT && maxLength > 0;
            },
            defaultValidationErrorMessage() {
                switch (this.tvrType) {
                    case TEXT_VALIDATION_RULE_TYPES.CHAR_SET:
                        return this.defaultCharSetValidationError;
                    case TEXT_VALIDATION_RULE_TYPES.PATTERN_MATCH:
                        return this.defaultPatternMatchValidationError;
                    default:
                        return "";
                }
            },
            ...mapI18n(KEYS, NAMESPACE)
        },
        mounted() {
            if (this.tvr !== null) {
                this.name = this.tvr.DisplayName;
                this.tvrType = this.tvr.TextValidationRuleTypeId;
                this.validationErrorMessage = this.tvr.ValidationErrorMessage ? this.tvr.ValidationErrorMessage : "";

                if (this.tvrType === TEXT_VALIDATION_RULE_TYPES.CHAR_SET) {
                    for (const charSet of this.tvr.RuleSchema.CharacterSets) {
                        switch (charSet) {
                            case TEXT_VALIDATION_RULE_CHAR_SETS.A_Z_LOWER:
                                this.aToZLower = true;
                                break;
                            case TEXT_VALIDATION_RULE_CHAR_SETS.A_Z_UPPER:
                                this.aToZUpper = true;
                                break;
                            case TEXT_VALIDATION_RULE_CHAR_SETS.NUMERIC:
                                this.numeric = true;
                                break;
                            case TEXT_VALIDATION_RULE_CHAR_SETS.PUNCTUATION:
                                this.punctuation = true;
                                break;
                        }
                    }

                    if (this.tvr.RuleSchema.MaxLength !== null) {
                        this.maxLength = this.tvr.RuleSchema.MaxLength;
                        this.enforceMaxLength = true;
                    }

                    this.patternMatchValues = [{ value: "" }];
                }

                if (this.tvrType === TEXT_VALIDATION_RULE_TYPES.PATTERN_MATCH) {
                    for (const pattern of this.tvr.RuleSchema.AllowedPatterns) {
                        this.patternMatchValues.push({ value: pattern });
                    }
                }
            } else {
                this.patternMatchValues = [{ value: "" }];
            }
        },
        methods: {
            onToggleEnforceMaxLength() {
                if (!this.enforceMaxLength) {
                    this.validationResults[this.validatorKeys.maxLength] = true;
                }
            },
            async isValidToClose(dialogResult) {
                if (dialogResult === EDIT_TVR_RESULT.save) {
                    return this.validateInputs();
                }

                return true;
            },
            _clone(obj) {
                return JSON.parse(JSON.stringify(obj));
            },
            onValidated(key, result) {
                // Must clone and set entire validationResults object to trigger
                // dependency updates
                const updatedResults = this._clone(this.validationResults);
                updatedResults[key] = result;
                this.validationResults = updatedResults;
            },
            validateInputs() {
                this.validationHub.validate();

                if (!this.inputsAreValid) {
                    return false;
                }

                if (this.tvrType === TEXT_VALIDATION_RULE_TYPES.PATTERN_MATCH) {
                    if (!this.atLeastOnePatternDefined) {
                        this.blockSaveMessage = this.blockSaveMessagePatternMatchNoneDefined;
                        this.showBlockSaveAlert = true;
                        return false;
                    }

                    if (this.areThereEmptyPatternsInTheConfig) {
                        this.blockSaveMessage = this.blockSaveMessagePatternMatch;
                        this.showBlockSaveAlert = true;
                        return false;
                    }
                }
                if (this.tvrType === TEXT_VALIDATION_RULE_TYPES.CHAR_SET) {
                    if (!this.atLeastOneCharacterSetSelected) {
                        this.blockSaveMessage = this.blockSaveMessageCharSet;
                        this.showBlockSaveAlert = true;
                        return false;
                    }
                }

                return true;
            },
            getResult() {
                if (!this.validateInputs()) {
                    return;
                }

                this.showBlockSaveAlert = false;

                const characterSets = [];
                let allowedPatterns = [];

                if (this.tvrType === TEXT_VALIDATION_RULE_TYPES.CHAR_SET) {
                    if (this.aToZUpper) characterSets.push(TEXT_VALIDATION_RULE_CHAR_SETS.A_Z_UPPER);
                    if (this.aToZLower) characterSets.push(TEXT_VALIDATION_RULE_CHAR_SETS.A_Z_LOWER);
                    if (this.numeric) characterSets.push(TEXT_VALIDATION_RULE_CHAR_SETS.NUMERIC);
                    if (this.punctuation) characterSets.push(TEXT_VALIDATION_RULE_CHAR_SETS.PUNCTUATION);
                }
                if (this.tvrType === TEXT_VALIDATION_RULE_TYPES.PATTERN_MATCH) {
                    allowedPatterns = this.getAllowedPatterns();
                }

                const errorMessage = this.validationErrorMessage !== null ? this.validationErrorMessage.trim() : "";

                return {
                    textValidationRule: {
                        DisplayName: collapseAndTrimWhitespace(this.name),
                        TextValidationRuleTypeId: this.tvrType,
                        RuleSchema: {
                            MaxLength: this.enforceMaxLength ? this.maxLength : null,
                            CharacterSets: characterSets,
                            AllowedPatterns: allowedPatterns
                        },
                        ValidationErrorMessage: errorMessage,
                        IsDeleted: false,
                        PublicId: this.tvr ? this.tvr.PublicId : null
                    }
                };
            },
            deletePattern(i) {
                this.patternMatchValues.splice(i, 1);
            },
            addNewPatternInput() {
                this.patternMatchValues.push({ value: "" });
            },
            getAltLabelForInput(i) {
                return "pattern-" + i;
            },
            getAllowedPatterns() {
                if (!this.patternMatchValues) {
                    return [];
                }

                return this.patternMatchValues.map((pattern) => collapseAndTrimWhitespace(pattern.value));
            },
            ignoreValidationError(key) {
                if (Number(key) === this.validatorKeys.maxLength && !this.enforceMaxLength) {
                    return true;
                }

                return false;
            },
            doesKeyHaveValidationError(key) {
                if (
                    key &&
                    this.validationResults.hasOwnProperty(key) &&
                    this.validationResults[key].hasError &&
                    !this.ignoreValidationError(key)
                ) {
                    return true;
                }

                return false;
            }
        }
    };
</script>
